import { useAppConfig } from '../hooks/useAppConfig';

export type ErrorLoggingConfig = {
  serverContextPath: string;
  serverUrl?: string;
  /**
   * Unikátní id klienta, např. u web aplikace se generuje při každém refresh stránky.
   */
  clientId: string;
  loggedUser?: string;
  /**
   * Verze aplikace, např. '22.0.0-SNAPSHOT'.
   */
  version?: string;
  /**
   * Build number - systémová, např. '123'.
   */
  buildNumber?: string;
  /**
   * Build version - systémová.
   */
  buildVersion?: string;
  additionalInfo: { [key: string]: unknown };
};

type StorageProps = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
};

export type ErrorLoggingProps = {
  setLoggedUser: (user?: string) => void;
  setVersions: (
    version?: string,
    buildNumber?: string,
    buildVersion?: string
  ) => void;
  setAdditionalInfo: (info: { [key: string]: unknown }) => void;
  errorConfig: ErrorLoggingConfig;
  logError: (stack: object | string) => Promise<void>;
  initStorage: (init: StorageProps) => void;
};

function generateUUID(): string {
  var dt = new Date().getTime();
  if (window.performance && typeof window.performance.now === 'function') {
    dt += performance.now(); //use high-precision timer if available
  }
  var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
    /[xy]/g,
    function (c) {
      var r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
    }
  );
  return uuid;
}

let appConfig = useAppConfig();
const errorConfig: ErrorLoggingConfig = {
  clientId: generateUUID(),
  version: appConfig.version,
  buildNumber: appConfig.buildNumber,
  buildVersion: appConfig.buildVersion,
  additionalInfo: {},
  serverContextPath: '',
};

let storageSettings: StorageProps = {
  getItem: async (key: string) => {
    return localStorage.getItem(key);
  },
  setItem: async (key: string, value: string) => {
    localStorage.setItem(key, value);
  },
};

type ErrorRecord = {
  id: string;
  stack: string;
  loggedUser?: string;
  clientId: string;
  time: string;
  version?: string;
  buildNumber?: string;
  buildVersion?: string;
  // Další možné doplňující informace k chybě nebo klientovi, např. u mobilních aplikací zde může být verze android apod.
  additionalInfo: { [key: string]: unknown };
};

const ERROR_STORAGE_KEY = 'app-error-logging-storage';

type ErrorLogging = {
  records: { [key: string]: ErrorRecord };
};

const emptyData: ErrorLogging = {
  records: {},
};

let memoryStorageData: ErrorLogging | undefined = undefined;

const getStorageData = async (): Promise<ErrorLogging> => {
  if (memoryStorageData) {
    return memoryStorageData;
  }

  let value = await storageSettings.getItem(ERROR_STORAGE_KEY);
  let data: ErrorLogging;

  try {
    data = value ? JSON.parse(value) : emptyData;
  } catch (e) {
    data = emptyData;
  }
  // console.log('storage data - get', data);

  memoryStorageData = data;
  return memoryStorageData;
};

const setStorageData = async (data: ErrorLogging) => {
  // console.log('storage data - set', data);
  await storageSettings.setItem(ERROR_STORAGE_KEY, JSON.stringify(data));
};

const pushRecord = async (record: ErrorRecord) => {
  // console.log('new error data', record);

  let data = await getStorageData();

  data.records[record.id] = record;

  await setStorageData(data);
};

const fixDuplicateRecords = async () => {
  let data = await getStorageData();
  if (!data || !data.records || Object.keys(data.records).length === 0) {
    return
  }

  const filteredRecords: ErrorRecord[] = []

  // Mapa klíč chyby (user + stack) -> počet výskytů
  const filteredDataMap: { [key: string]:  number } = {};
  Object.values(data.records).forEach(r => {
    const key = `${r.loggedUser || ''}-${r.stack}`;
    if (filteredDataMap[key] === undefined) {
      filteredDataMap[key] = 1;
      filteredRecords.push(r);
    } else {
      filteredDataMap[key]++;
    }
  })

  const newResords: { [key: string]: ErrorRecord } = {};
  filteredRecords.forEach(r => {
    newResords[r.id] = r;

    const key = `${r.loggedUser || ''}-${r.stack}`;
    if (filteredDataMap[key] > 1) {
      r.stack = `(${filteredDataMap[key]}x) ${r.stack}`;
    }
  })
  data.records = newResords;

  await setStorageData(data);
}

/**
 * Vrátí prvních maxCount záznamů pro odeslání na server.
 * @param maxCount
 */
const getRecordsForUpload = async (maxCount: number): Promise<ErrorRecord[]> => {
  let data = await getStorageData();
  // console.log('GETTING records from', data.records);
  let ids = Object.keys(data.records).slice(0, maxCount);
  return ids.map((id) => data.records[id]);
};

const deleteRecords = async (records: ErrorRecord[]) => {
  let data = await getStorageData();

  records.forEach((r) => {
    delete data.records[r.id];
  });

  await setStorageData(data);
};

export const initErrorLogging = async (
  serverContextPath: string,
  serverUrl?: string
) => {
  errorConfig.serverContextPath = serverContextPath;
  errorConfig.serverUrl = serverUrl || '';
  await getStorageData();
};

export const startErrorLoggingUpload = () => {
  // console.log('STARTING UPLOAD');
  doUpload();
};

async function postData(url = '', data = {}) {
  // Default options are marked with *
  const response = await fetch(url, {
    method: 'POST', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    credentials: 'same-origin', // include, *same-origin, omit
    headers: {
      'Content-Type': 'application/json',
      // 'Content-Type': 'application/x-www-form-urlencoded',
    },
    redirect: 'follow', // manual, *follow, error
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
    body: JSON.stringify(data), // body data type must match "Content-Type" header
  });
  return response; //.json(); // parses JSON response into native JavaScript objects
}

type ErrorLoggingVO = {
  records: ErrorRecord[];
};

const doUpload = async () => {
  // Odstraní duplicitní záznamy
  await fixDuplicateRecords();

  // Načte záznamy pro odeslání
  let recodrs = await getRecordsForUpload(100);

  if (recodrs.length > 0) {
    // console.log('@@@ UPLOADING records', recodrs.length, recodrs);
    const vo: ErrorLoggingVO = {
      records: recodrs,
    };

    try {
      const res = await postData(
        `${errorConfig.serverUrl}${errorConfig.serverContextPath}/api/logging/client/error`,
        vo
      );

      if (res.ok) {
        await deleteRecords(recodrs);
      }
    } catch (e) {
      // console.error('Chyba odeslání chyb aplikace na server', e);
    }
  } else {
    // console.log('no data for upload');
  }

  setTimeout(doUpload, 60000);
};

export const useErrorLogging = (): ErrorLoggingProps => {
  return {
    errorConfig,
    setLoggedUser: (loggedUser?: string) => {
      errorConfig.loggedUser = loggedUser;
    },
    setVersions: (
      version?: string,
      buildNumber?: string,
      buildVersion?: string
    ) => {
      errorConfig.version = version;
      errorConfig.buildNumber = buildNumber;
      errorConfig.buildVersion = buildVersion;
    },
    setAdditionalInfo: (additionalInfo: { [key: string]: unknown }) => {
      errorConfig.additionalInfo = additionalInfo;
    },
    logError: async (stack: object | string) => {
      const record: ErrorRecord = {
        id: generateUUID(),
        stack: `${stack}`,
        additionalInfo: errorConfig.additionalInfo,
        loggedUser: errorConfig.loggedUser,
        clientId: errorConfig.clientId,
        version: errorConfig.version,
        buildNumber: errorConfig.buildNumber,
        buildVersion: errorConfig.buildVersion,
        time: getJavaDateTime(new Date()),
      };
      // console.log("@@@new error", record);
      await pushRecord(record);
    },
    initStorage: (storage: StorageProps) => {
      storageSettings = storage;
    },
  };
};

export function getJavaDateTime(date: Date) {
  const localDate = new Date(+date);
  localDate.setMinutes(localDate.getMinutes() + -localDate.getTimezoneOffset());
  return localDate.toISOString(); //.split('.')[0];
}
